/*
 * cart-limits.js — single source of truth for cart quantity limits.
 *
 * Loaded synchronously in <head> BEFORE {{ content_for_header }} and every
 * theme script, so:
 *   1. window.CartLimits exists before any other script runs.
 *   2. Its fetch guard is the INNERMOST window.fetch wrapper. Wrappers added
 *      later (custom-cart.js, block-buy-buttons, apps) call through it and just
 *      see a normal Shopify-style 422 when a request is blocked.
 *
 * Config: window.CART_LIMIT_CONFIG, rendered from theme settings in theme.liquid.
 *
 * Scripts that integrate explicitly (product cards, PDP buy block) call
 * reserve() before add.js and send the X-Cart-Limit-Checked header so the
 * guard doesn't count the same request twice. Anything else (bundles, UV
 * index, quick add, reorder, apps in this window) is checked by the guard.
 *
 * Decreases are never blocked.
 */
(function () {
  "use strict";

  if (window.CartLimits) return; // loaded twice — keep the first instance

  var HEADER = "X-Cart-Limit-Checked";
  var CHANGE_EVENT = "cart-limits:change";
  var nativeFetch = window.fetch.bind(window);

  // ─── Config ────────────────────────────────────────────────────────────────
  var raw = window.CART_LIMIT_CONFIG || {};
  var enabled = raw.enabled !== false;
  var maxCartItems = Number(raw.maxCartItems) > 0 ? Number(raw.maxCartItems) : null;
  var messages = raw.messages || {};
  // Sitewide per-product limit (null = off). Applies only to products that are
  // in no rule; a product's own rule always decides, above or below this.
  var defaultProductLimit = Number(raw.defaultProductLimit) > 0 ? Number(raw.defaultProductLimit) : null;

  // productLimits: { "<product_id>": maxUnits }. Built from rules; if a product
  // is in several rules the lowest limit wins.
  var productLimits = {};
  (raw.rules || []).forEach(function (rule) {
    var max = Number(rule && rule.max);
    if (!Number.isFinite(max) || max <= 0) return;
    (rule.products || []).forEach(function (id) {
      var key = String(id);
      if (productLimits[key] == null || max < productLimits[key]) productLimits[key] = max;
    });
  });

  function format(template, max) {
    return String(template).replace(/\{max\}/g, max);
  }
  function cartMessage() {
    return format(messages.cart || "You can add a maximum of {max} items per order.", maxCartItems);
  }
  function productMessage(max) {
    return format(messages.product || "You can add a maximum of {max} units of this product per order.", max);
  }

  // ─── State ─────────────────────────────────────────────────────────────────
  var cartCount = null;              // confirmed/optimistic cart.item_count
  var productQty = new Map();        // product_id → units in cart
  var lineInfo = new Map();          // line key / variant id → { productId, qty }
  var lineOrder = [];                // line keys in cart order (for change.js `line`)
  var pendingCart = 0;               // units in flight
  var pendingByProduct = new Map();  // product_id → units in flight
  var variantToProduct = new Map();  // variant_id → product_id

  function emitChange() {
    try {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    } catch (_) {}
  }

  function registerVariant(variantId, productId) {
    if (variantId != null && productId != null && variantId !== "" && productId !== "") {
      variantToProduct.set(String(variantId), String(productId));
    }
  }

  function productIdForVariant(variantId) {
    if (variantId == null) return null;
    var key = String(variantId);
    if (variantToProduct.has(key)) return variantToProduct.get(key);
    // PDP fallback. ShopifyAnalytics.meta is not a documented API, so this is
    // best effort only; integrated scripts pass the product id explicitly.
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    if (meta && meta.id && Array.isArray(meta.variants) &&
        meta.variants.some(function (v) { return String(v.id) === key; })) {
      registerVariant(key, meta.id);
      return String(meta.id);
    }
    return null;
  }

  var lastCountsSig = null;
  function record(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var countsSig = cart.item_count + "|" + cart.items
      .map(function (i) { return i.key + ":" + i.product_id + ":" + i.quantity; }).join(",");
    if (countsSig === lastCountsSig && Number.isFinite(cartCount)) return;
    lastCountsSig = countsSig;
    cartCount = Number(cart.item_count) || 0;
    productQty = new Map();
    lineInfo = new Map();
    lineOrder = [];
    cart.items.forEach(function (item) {
      var pid = String(item.product_id);
      var qty = Number(item.quantity) || 0;
      registerVariant(item.variant_id, pid);
      productQty.set(pid, (productQty.get(pid) || 0) + qty);
      if (item.key) {
        lineInfo.set(String(item.key), { productId: pid, qty: qty, variantId: String(item.variant_id) });
        lineOrder.push(String(item.key));
      }
    });
    emitChange();
  }

  var countsPromise = null;
  function refreshCounts() {
    if (countsPromise) return countsPromise;
    countsPromise = nativeFetch("/cart.js?ts=" + Date.now(), { cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(record)
      .catch(function (err) { console.error("CartLimits: cart.js failed", err); })
      .then(function () { countsPromise = null; });
    return countsPromise;
  }

  function ensureCounts() {
    return Number.isFinite(cartCount) ? Promise.resolve() : refreshCounts();
  }

  // change/update/clear.js responses ARE the full cart: read counts from a
  // clone of the body instead of issuing another GET /cart.js.
  function recordFromResponse(res) {
    try {
      res.clone().json().then(record, function () {});
    } catch (_) {}
  }

  function getProductLimit(productId) {
    if (productId == null) return null; // product unknown → only the cart limit can apply
    var rule = productLimits[String(productId)];
    if (rule != null) return rule;         // the product's own rule always wins
    return defaultProductLimit;            // no rule → sitewide (or none)
  }

  // ─── Checks ────────────────────────────────────────────────────────────────
  // Adding `qty` units of `productId` on top of cart + in-flight units.
  // Fails open while counts are unknown (reserve() awaits them first).
  function check(productId, qty) {
    if (!enabled) return null;
    qty = Number(qty) || 1;
    var count = Number(cartCount) || 0;

    if (maxCartItems !== null && count + pendingCart + qty > maxCartItems) {
      return cartMessage();
    }

    var limit = getProductLimit(productId);
    if (limit !== null) {
      var key = String(productId);
      var inCart = productQty.get(key) || 0;
      var pending = pendingByProduct.get(key) || 0;
      if (inCart + pending + qty > limit) return productMessage(limit);
    }
    return null;
  }

  // items: [{ productId, qty }] — for multi-line adds (bundles).
  function checkMany(items) {
    if (!enabled) return null;
    var total = items.reduce(function (s, i) { return s + i.qty; }, 0);
    var count = Number(cartCount) || 0;
    if (maxCartItems !== null && count + pendingCart + total > maxCartItems) {
      return cartMessage();
    }
    var byProduct = new Map();
    items.forEach(function (i) {
      if (i.productId == null) return;
      var k = String(i.productId);
      byProduct.set(k, (byProduct.get(k) || 0) + i.qty);
    });
    var msg = null;
    byProduct.forEach(function (qty, pid) {
      if (msg) return;
      var limit = getProductLimit(pid);
      if (limit === null) return;
      if ((productQty.get(pid) || 0) + (pendingByProduct.get(pid) || 0) + qty > limit) {
        msg = productMessage(limit);
      }
    });
    return msg;
  }

  function addPending(productId, qty) {
    pendingCart += qty;
    if (productId != null) {
      var k = String(productId);
      pendingByProduct.set(k, (pendingByProduct.get(k) || 0) + qty);
    }
  }

  function release(productId, qty) {
    qty = Number(qty) || 1;
    pendingCart = Math.max(0, pendingCart - qty);
    if (productId != null) {
      var k = String(productId);
      var next = (pendingByProduct.get(k) || 0) - qty;
      if (next > 0) pendingByProduct.set(k, next);
      else pendingByProduct.delete(k);
    }
    emitChange();
  }

  // Check + reserve with no await in between, so concurrent clicks from any
  // script can't both pass at the boundary.
  function reserve(productId, qty) {
    qty = Number(qty) || 1;
    return ensureCounts().then(function () {
      var message = check(productId, qty);
      if (message) return { ok: false, message: message };
      addPending(productId, qty);
      emitChange();
      return { ok: true };
    });
  }

  // Optimistic count bump after a successful add; the next cart sync confirms.
  function commit(productId, qty) {
    qty = Number(qty) || 1;
    lastCountsSig = null;
    cartCount = (Number(cartCount) || 0) + qty;
    if (productId != null) {
      var k = String(productId);
      productQty.set(k, (productQty.get(k) || 0) + qty);
    }
    emitChange();
  }

  function isCartBlocked() {
    return enabled && maxCartItems !== null && Number.isFinite(cartCount) &&
      cartCount + pendingCart >= maxCartItems;
  }

  function isProductBlocked(productId) {
    if (!enabled) return false;
    var limit = getProductLimit(productId);
    if (limit === null) return false;
    var k = String(productId);
    return (productQty.get(k) || 0) + (pendingByProduct.get(k) || 0) >= limit;
  }

  function isBlocked(productId) {
    return isCartBlocked() || isProductBlocked(productId);
  }

  function resetPending() {
    pendingCart = 0;
    pendingByProduct.clear();
    emitChange();
  }

  // ─── Shared toast (one element for every script) ───────────────────────────
  var toastTimer = null;
  function notify(message) {
    if (!message || !document.body) return;
    var toast = document.querySelector(".cart-limit-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "cart-limit-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.style.cssText =
        "position:fixed;left:50%;bottom:90px;transform:translateX(-50%);" +
        "max-width:90vw;padding:10px 16px;border-radius:9999px;background:#111;" +
        "color:#fff;font-size:14px;line-height:1.3;text-align:center;z-index:2147483000;" +
        "box-shadow:0 4px 16px rgba(0,0,0,.2);pointer-events:none;display:none;";
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.display = "block";
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.style.display = "none"; }, 3500);
  }

  // ─── Request parsing (guard) ───────────────────────────────────────────────
  function hasHeader(input, init) {
    var h = (init && init.headers) ||
      (typeof Request !== "undefined" && input instanceof Request ? input.headers : null);
    if (!h) return false;
    var name = HEADER.toLowerCase();
    if (typeof Headers !== "undefined" && h instanceof Headers) return h.has(name);
    if (Array.isArray(h)) return h.some(function (p) { return String(p[0]).toLowerCase() === name; });
    return Object.keys(h).some(function (k) { return k.toLowerCase() === name; });
  }

  function readBody(init) {
    var body = init && init.body;
    if (typeof body === "string") {
      var t = body.trim();
      if (t.charAt(0) === "{") return { json: JSON.parse(t) };
      return { params: new URLSearchParams(t) };
    }
    if ((typeof FormData !== "undefined" && body instanceof FormData) || body instanceof URLSearchParams) {
      return { params: body };
    }
    return null;
  }

  // add.js → [{ productId, qty }]. Unknown shapes → one unit, unknown product
  // (the cart-level cap still applies).
  function parseAdd(init) {
    try {
      var b = readBody(init);
      if (b && b.json) {
        var j = b.json;
        var lines = Array.isArray(j.items) ? j.items : [j];
        return lines.map(function (i) {
          return { productId: productIdForVariant(i.id), qty: Number(i.quantity) || 1 };
        });
      }
      if (b && b.params) {
        return [{ productId: productIdForVariant(b.params.get("id")), qty: Number(b.params.get("quantity")) || 1 }];
      }
    } catch (_) {}
    return [{ productId: null, qty: 1 }];
  }

  function findLine(ref) {
    if (ref == null) return null;
    var key = String(ref);
    if (lineInfo.has(key)) return lineInfo.get(key);
    var found = null; // numeric id = variant id → first matching line
    lineInfo.forEach(function (info) { if (!found && info.variantId === key) found = info; });
    return found;
  }

  // change.js / update.js → list of { productId, delta } for lines that grow.
  // Anything unresolvable is skipped (fail open): decreases must never break.
  function parseQuantityChanges(url, init) {
    var out = [];
    try {
      var b = readBody(init);
      if (!b) return out;
      var get = function (k) { return b.json ? b.json[k] : b.params.get(k); };

      if (/\/cart\/change/.test(url)) {
        var qty = Number(get("quantity"));
        if (!Number.isFinite(qty)) return out;
        var line = get("line");
        var info = line != null ? lineInfo.get(lineOrder[Number(line) - 1]) : findLine(get("id"));
        if (info && qty > info.qty) out.push({ productId: info.productId, qty: qty - info.qty });
        return out;
      }

      if (/\/cart\/update/.test(url) && b.json && b.json.updates) {
        var updates = b.json.updates;
        var pairs = Array.isArray(updates)
          ? updates.map(function (q, idx) { return [lineOrder[idx], q]; })
          : Object.keys(updates).map(function (k) { return [k, updates[k]]; });
        pairs.forEach(function (p) {
          var inf = findLine(p[0]);
          var q = Number(p[1]);
          if (inf && Number.isFinite(q) && q > inf.qty) out.push({ productId: inf.productId, qty: q - inf.qty });
        });
      }
    } catch (_) {}
    return out;
  }

  function limitResponse(message) {
    return new Response(
      JSON.stringify({ status: 422, message: "Cart limit reached", description: message }),
      { status: 422, headers: { "Content-Type": "application/json" } }
    );
  }

  // ─── Network guard (innermost fetch wrapper) ───────────────────────────────
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
    var isChange = /\/cart\/(change|update)(\.js)?(\?|$)/.test(url);
    var isMutation = isAdd || isChange || /\/cart\/clear(\.js)?(\?|$)/.test(url);

    if (!enabled || !isMutation) return nativeFetch(input, init);

    // Integrated scripts already reserved; just keep counts fresh afterwards.
    if (hasHeader(input, init)) {
      return nativeFetch(input, init).then(function (res) {
        if (res.ok && !isAdd) recordFromResponse(res);
        return res;
      });
    }

    return ensureCounts().then(function () {
      var reserved = [];

      if (isAdd) {
        var items = parseAdd(init);
        var msg = checkMany(items);
        if (msg) { notify(msg); return limitResponse(msg); }
        reserved = items;
      } else if (isChange) {
        var grows = parseQuantityChanges(url, init);
        if (grows.length) {
          var msg2 = checkMany(grows);
          if (msg2) { notify(msg2); return limitResponse(msg2); }
          reserved = grows;
        }
      }

      reserved.forEach(function (i) { addPending(i.productId, i.qty); });

      return nativeFetch(input, init).then(function (res) {
        reserved.forEach(function (i) { release(i.productId, i.qty); });
        if (res.ok) {
          // add.js: optimistic bump; the theme's own cart sync (custom-cart.js
          // applyCart → CartLimits.record) confirms it. No extra /cart.js here.
          if (isAdd) reserved.forEach(function (i) { commit(i.productId, i.qty); });
          else recordFromResponse(res);
        }
        return res;
      }, function (err) {
        reserved.forEach(function (i) { release(i.productId, i.qty); });
        throw err;
      });
    });
  };

  // ─── Native form posts to /cart/add (no JS handler) ────────────────────────
  // Capture phase so it runs before theme submit handlers.
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!enabled || !form || !form.action || !/\/cart\/add/.test(form.action)) return;
    if (!Number.isFinite(cartCount)) return; // unknown yet → let it through
    var data = new FormData(form);
    var productId = productIdForVariant(data.get("id")) || form.dataset.productId || null;
    var msg = check(productId, Number(data.get("quantity")) || 1);
    if (msg) {
      e.preventDefault();
      e.stopImmediatePropagation();
      notify(msg);
    }
  }, true);

  // ─── bfcache: in-flight reservations died with the page snapshot ──────────
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) resetPending();
  });

  window.CartLimits = {
    HEADER: HEADER,
    CHANGE_EVENT: CHANGE_EVENT,
    get enabled() { return enabled; },
    record: record,
    refresh: refreshCounts,
    ensureCounts: ensureCounts,
    check: check,
    reserve: reserve,
    release: release,
    commit: commit,
    registerVariant: registerVariant,
    getProductLimit: getProductLimit,
    isCartBlocked: isCartBlocked,
    isProductBlocked: isProductBlocked,
    isBlocked: isBlocked,
    resetPending: resetPending,
    notify: notify,
  };

  // No page-load /cart.js read: counts arrive with custom-cart.js's initial
  // sync (applyCart → record). ensureCounts() reads only if a check happens
  // before that.
})();
